import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import VerifiedRounded from '@mui/icons-material/VerifiedRounded';
import { useAuth } from '@/auth';
import { SharingCard } from '@/clients/SharingCard';
import { DriveCard } from '@/integrations/drive/DriveCard';
import { PrivacyCard } from '@/privacy/PrivacyCard';
import { UserAvatar } from '@/components/UserAvatar';
import { useMe } from '@/session/useSession';
import { useTenant } from '@/tenant/useTenant';
import { PageHeader } from './PageHeader';

const ROLE_LABEL: Record<string, string> = {
  platform_admin: 'Admin da plataforma',
  tenant_admin: 'Administração do espaço',
  professional: 'Profissional',
  end_user: 'Cliente',
};

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.25, sm: 2 }} sx={{ py: 1.25 }}>
      <Typography variant="body2" color="text.secondary" sx={{ width: { sm: 180 }, flexShrink: 0 }}>
        {label}
      </Typography>
      <Box sx={{ minWidth: 0, wordBreak: 'break-word' }}>{children}</Box>
    </Stack>
  );
}

/** The signed-in person, their access in this space and privacy information. */
export function AccountPage() {
  const { user, signOut } = useAuth();
  const me = useMe();
  const { displayName, isPlatform, isMarketplace } = useTenant();

  return (
    <>
      <PageHeader title="Minha conta" subtitle={`Seu acesso em ${displayName}.`} />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center', py: 4 }}>
              <UserAvatar
                user={user}
                sx={{ width: 88, height: 88, mx: 'auto', fontSize: '2rem', mb: 2 }}
              />
              <Typography variant="h5">{me.user.name ?? 'Sem nome'}</Typography>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {me.user.email}
              </Typography>
              {me.user.emailVerified && (
                <Chip
                  size="small"
                  variant="soft"
                  color="success"
                  icon={<VerifiedRounded />}
                  label="E-mail verificado"
                />
              )}
              <Box>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<LogoutRounded />}
                  sx={{ mt: 3 }}
                  onClick={() => void signOut()}
                >
                  Sair
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" sx={{ mb: 1 }}>
                Acesso
              </Typography>
              <Row label="Espaço">{displayName}</Row>
              <Divider />
              <Row label="Funções">
                <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1 }}>
                  {me.roles.map((role) => (
                    <Chip key={role} size="small" variant="soft" label={ROLE_LABEL[role] ?? role} />
                  ))}
                </Stack>
              </Row>
              <Divider />
              <Row label="Login">
                {user?.signInMethod === 'google' ? 'Conta Google' : 'Acesso de desenvolvimento'}
              </Row>
              <Divider />
              <Row label="Privacidade">
                <Typography variant="body2" color="text.secondary">
                  Seus dados ficam isolados neste espaço. Fotos e documentos pessoais ficam no seu
                  próprio Google Drive, conectado apenas quando você decidir usar um recurso que
                  precise dele.
                </Typography>
              </Row>
            </CardContent>
          </Card>
        </Grid>
        {!isPlatform && !isMarketplace && me.roles.includes('end_user') && (
          <Grid size={12}>
            <SharingCard />
          </Grid>
        )}
        {!isPlatform && !isMarketplace && me.permissions.includes('storage.self') && (
          <Grid size={12}>
            <DriveCard />
          </Grid>
        )}
        {!isPlatform && (
          <Grid size={12}>
            <PrivacyCard />
          </Grid>
        )}
      </Grid>
    </>
  );
}
